import { AccessTime as AccessTimeIcon } from "@mui/icons-material";
import { Box, Chip, Skeleton, Typography } from "@mui/material";
import { format, isSameMinute, startOfDay } from "date-fns";
import React, { useEffect, useState } from "react";
import {
  DEFAULT_MAX_SLOTS,
  formatDayLabel,
  pickSpread,
  WORK_DAY_END_HOUR,
  WORK_DAY_START_HOUR,
} from "./freeSlotUtils";
import { useFreeSlots } from "./useFreeSlots";

interface FreeSlotChipsProps {
  date: Date | null;
  durationMinutes: number;
  onSelectSlot: (slot: Date) => void;
  selectedTime?: Date | null;
  excludeCalendarEventId?: string;
  excludeWorkItemId?: number;
  maxSlots?: number;
  dense?: boolean;
  disabled?: boolean;
}

const FreeSlotChips: React.FC<FreeSlotChipsProps> = ({
  date,
  durationMinutes,
  onSelectSlot,
  selectedTime,
  excludeCalendarEventId,
  excludeWorkItemId,
  maxSlots,
  dense = false,
  disabled = false,
}) => {
  const { slots, hasAllDayEvent, allDayTitles, isLoading, isError } =
    useFreeSlots(date, durationMinutes, {
      excludeCalendarEventId,
      excludeWorkItemId,
    });

  const validDate = date && !isNaN(date.getTime()) ? date : null;

  // Visa ett spritt urval som default, med möjlighet att expandera till alla
  const [showAll, setShowAll] = useState(false);
  const dayKey = validDate ? format(startOfDay(validDate), "yyyy-MM-dd") : null;
  useEffect(() => {
    setShowAll(false);
  }, [dayKey]);

  const max = maxSlots ?? DEFAULT_MAX_SLOTS;
  const visibleSlots = showAll ? slots : pickSpread(slots, max);
  const hiddenCount = slots.length - visibleSlots.length;
  const dayIsPast =
    validDate !== null && startOfDay(validDate) < startOfDay(new Date());

  if (!validDate || dayIsPast || isError) {
    return null;
  }

  const chipSx = dense
    ? { height: 24, fontSize: "0.7rem" }
    : undefined;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {isLoading
          ? `Lediga tider ${formatDayLabel(validDate)}:`
          : slots.length === 0
            ? `Inga lediga tider ${formatDayLabel(validDate)} (${String(
                WORK_DAY_START_HOUR
              ).padStart(2, "0")}–${WORK_DAY_END_HOUR})`
            : hiddenCount > 0
              ? `Lediga tider ${formatDayLabel(validDate)} – visar ${
                  visibleSlots.length
                } av ${slots.length}:`
              : `Lediga tider ${formatDayLabel(validDate)} (${slots.length} st):`}
      </Typography>

      {hasAllDayEvent && (
        <Typography
          variant="caption"
          sx={{ display: "block", mb: 0.5, color: "warning.main" }}
        >
          ⚠ Heldag: {allDayTitles.join(", ")}
        </Typography>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {isLoading
          ? [1, 2, 3].map((key) => (
              <Skeleton
                key={key}
                variant="rounded"
                width={56}
                height={dense ? 24 : 24}
                sx={{ borderRadius: 4 }}
              />
            ))
          : (
              <>
                {visibleSlots.map((slot) => {
                  const isSelected =
                    selectedTime != null &&
                    !isNaN(selectedTime.getTime()) &&
                    isSameMinute(slot, selectedTime);
                  return (
                    <Chip
                      key={slot.getTime()}
                      size="small"
                      clickable
                      disabled={disabled}
                      icon={<AccessTimeIcon />}
                      label={format(slot, "HH:mm")}
                      variant={isSelected ? "filled" : "outlined"}
                      color={isSelected ? "primary" : "default"}
                      onClick={() => onSelectSlot(slot)}
                      sx={{
                        ...chipSx,
                        "&:hover": {
                          bgcolor: isSelected ? undefined : "primary.light",
                          color: isSelected ? undefined : "white",
                        },
                      }}
                    />
                  );
                })}
                {hiddenCount > 0 && (
                  <Chip
                    size="small"
                    clickable
                    disabled={disabled}
                    label={`+${hiddenCount} till`}
                    variant="outlined"
                    onClick={() => setShowAll(true)}
                    sx={{ ...chipSx, borderStyle: "dashed" }}
                  />
                )}
                {showAll && slots.length > max && (
                  <Chip
                    size="small"
                    clickable
                    disabled={disabled}
                    label="Visa färre"
                    variant="outlined"
                    onClick={() => setShowAll(false)}
                    sx={{ ...chipSx, borderStyle: "dashed" }}
                  />
                )}
              </>
            )}
      </Box>
    </Box>
  );
};

export default FreeSlotChips;
